// Comercial > Metas (Fase 2 — 2026-05-29 / edição em massa 2026-05-30)
// Tabela com 12 meses do ano selecionado. Cada célula Meta é input direto;
// botão "Salvar alterações" no topo grava tudo de uma vez (loop chamando
// o endpoint salvar existente, sem precisar de bulk no backend).
//
// Granularidade: 1 meta por mês pra empresa toda (decisão Lucas).
// Permissão: admin + diretor_operador (perm `comercial.metas.editar`).
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Card, Typography, Table, Button, InputNumber, App as AntApp, Flex, Tag,
  Progress, Select, Space, Statistic, Row, Col, Input, Empty, Spin, Popconfirm, Alert,
} from 'antd';
import {
  SaveOutlined, UndoOutlined, DeleteOutlined, CalendarOutlined,
  ArrowUpOutlined, DollarOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';
import { usePerms } from '../hooks/usePerms';

const { Title, Text, Paragraph } = Typography;

interface MesMeta {
  periodo: string;            // YYYY-MM
  meta_valor: number;
  meta_quantidade: number | null;
  observacoes: string | null;
  realizado: number;
  n_ganhos: number;
  pct_atingimento: number | null;
  atualizado_em: string | null;
  atualizado_por: string | null;
}

interface MetasResp {
  ano: number;
  meses: MesMeta[];
  totais: {
    meta_valor: number;
    realizado: number;
    n_ganhos: number;
    pct_atingimento: number | null;
  };
}

interface MetaEditada {
  meta_valor: number;
  meta_quantidade: number | null;
  observacoes: string;
}

const fmt = (v: number) => 'R$ ' + (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const MESES_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

function pctColor(pct: number | null): string {
  if (pct == null) return '#888';
  if (pct >= 100) return '#0a7f4a';
  if (pct >= 70) return '#B07500';
  return '#B91626';
}

function ComercialMetasInner() {
  const { message, modal } = AntApp.useApp();
  const { pode } = usePerms();
  const podeEditar = pode('comercial.metas.editar');
  const [ano, setAno] = useState<number>(new Date().getFullYear());
  const [data, setData] = useState<MetasResp | null>(null);
  const [loading, setLoading] = useState(true);
  // Mapa de edições pendentes: periodo → valores editados.
  // Quando linha bate com o original do banco, removemos do mapa.
  const [editados, setEditados] = useState<Record<string, MetaEditada>>({});
  const [salvando, setSalvando] = useState(false);
  const [progresso, setProgresso] = useState<{ atual: number; total: number } | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/comercial/metas?ano=${ano}`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setData(d);
      setEditados({});  // recarga sempre limpa edits pendentes
    } catch (e: any) {
      message.error('Erro: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }, [ano, message]);

  useEffect(() => { carregar(); }, [carregar]);

  // Retorna o valor exibido na célula (editado se houver, senão original)
  function getCampo<K extends keyof MetaEditada>(m: MesMeta, campo: K): MetaEditada[K] {
    const e = editados[m.periodo];
    if (e && campo in e) return e[campo];
    if (campo === 'meta_valor') return (m.meta_valor || 0) as MetaEditada[K];
    if (campo === 'meta_quantidade') return (m.meta_quantidade ?? null) as MetaEditada[K];
    return ((m.observacoes ?? '') as string) as MetaEditada[K];
  }

  function setCampo<K extends keyof MetaEditada>(m: MesMeta, campo: K, valor: MetaEditada[K]) {
    setEditados(prev => {
      // Compara com o original — se voltou ao valor banco, remove do mapa
      const atual: MetaEditada = prev[m.periodo] || {
        meta_valor: m.meta_valor || 0,
        meta_quantidade: m.meta_quantidade ?? null,
        observacoes: m.observacoes ?? '',
      };
      const novo = { ...atual, [campo]: valor };
      const original: MetaEditada = {
        meta_valor: m.meta_valor || 0,
        meta_quantidade: m.meta_quantidade ?? null,
        observacoes: m.observacoes ?? '',
      };
      const ehIgualOriginal = novo.meta_valor === original.meta_valor
        && novo.meta_quantidade === original.meta_quantidade
        && novo.observacoes === original.observacoes;
      const next = { ...prev };
      if (ehIgualOriginal) delete next[m.periodo];
      else next[m.periodo] = novo;
      return next;
    });
  }

  function linhaEditada(periodo: string) {
    return !!editados[periodo];
  }

  async function salvarTudo() {
    const entries = Object.entries(editados);
    if (entries.length === 0) return;
    setSalvando(true);
    setProgresso({ atual: 0, total: entries.length });
    const token = await getToken();
    let ok = 0;
    const erros: { periodo: string; erro: string }[] = [];
    for (let i = 0; i < entries.length; i++) {
      const [periodo, edit] = entries[i];
      try {
        await axios.post(`${API_URL}/comercial/metas`, {
          action: 'salvar',
          periodo,
          meta_valor: edit.meta_valor,
          meta_quantidade: edit.meta_quantidade,
          observacoes: edit.observacoes,
        }, { headers: { Authorization: token } });
        ok++;
      } catch (e: any) {
        erros.push({ periodo, erro: e.response?.data?.error || e.message });
      }
      setProgresso({ atual: i + 1, total: entries.length });
    }
    setSalvando(false);
    setProgresso(null);
    if (erros.length === 0) {
      message.success(`${ok} meta(s) salva(s).`);
    } else if (ok === 0) {
      message.error(`Todas as ${erros.length} falharam. Ex: ${erros[0].erro}`);
    } else {
      message.warning(`${ok} salvas, ${erros.length} falharam. Ex: ${erros[0].periodo}: ${erros[0].erro}`);
    }
    await carregar();
  }

  function descartarTudo() {
    if (Object.keys(editados).length === 0) return;
    setEditados({});
    message.info('Alterações descartadas.');
  }

  async function excluirMeta(m: MesMeta) {
    modal.confirm({
      title: `Excluir meta de ${MESES_PT[parseInt(m.periodo.split('-')[1], 10) - 1]}?`,
      content: 'A meta será removida. Realizado continua sendo calculado normalmente.',
      okText: 'Excluir', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/comercial/metas`,
            { action: 'excluir', periodo: m.periodo },
            { headers: { Authorization: token } });
          message.success('Meta excluída.');
          await carregar();
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  const cols: ColumnsType<MesMeta> = [
    {
      title: 'Mês', dataIndex: 'periodo', key: 'p', width: 120,
      render: (p: string) => {
        const m = parseInt(p.split('-')[1], 10);
        return <Text strong style={{ color: '#020D7F' }}>{MESES_PT[m - 1]}</Text>;
      },
    },
    {
      title: 'Meta (R$)', key: 'mv', width: 170,
      render: (_, row) => {
        const v = getCampo(row, 'meta_valor');
        const editado = linhaEditada(row.periodo);
        return (
          <InputNumber size="small" value={v || null}
            disabled={!podeEditar}
            onChange={(novo) => setCampo(row, 'meta_valor', (novo as number) || 0)}
            min={0} step={1000}
            style={{
              width: '100%',
              borderColor: editado ? '#0930D0' : undefined,
              boxShadow: editado ? '0 0 0 1px #BAD8FF' : undefined,
            }}
            placeholder="0"
            formatter={(val) => `R$ ${(val || 0).toLocaleString('pt-BR')}`}
            parser={(val) => Number(String(val).replace(/[^\d]/g, '')) as any} />
        );
      },
    },
    {
      title: 'Qtd alvo', key: 'mq', width: 80,
      render: (_, row) => {
        const v = getCampo(row, 'meta_quantidade');
        const editado = linhaEditada(row.periodo);
        return (
          <InputNumber size="small" value={v as number | null}
            disabled={!podeEditar}
            onChange={(novo) => setCampo(row, 'meta_quantidade', (novo as number) ?? null)}
            min={0} max={1000} placeholder="—"
            style={{
              width: '100%',
              borderColor: editado ? '#0930D0' : undefined,
            }} />
        );
      },
    },
    {
      title: 'Realizado', dataIndex: 'realizado', key: 'r', width: 140, align: 'right',
      render: (v: number, row) => (
        <div>
          <Text style={{ color: '#0a7f4a', fontWeight: 600, whiteSpace: 'nowrap' }}>{fmt(v)}</Text>
          <div><Text type="secondary" style={{ fontSize: 10 }}>{row.n_ganhos} ganho(s)</Text></div>
        </div>
      ),
    },
    {
      title: '%', dataIndex: 'pct_atingimento', key: 'pct', width: 130,
      render: (pct: number | null) => pct == null
        ? <Text type="secondary">—</Text>
        : <Progress percent={Math.min(pct, 100)} size="small" strokeColor={pctColor(pct)}
            format={() => <Text style={{ color: pctColor(pct), fontWeight: 600, fontSize: 11 }}>{pct}%</Text>} />,
    },
    {
      title: 'Observações', key: 'obs', ellipsis: true,
      render: (_, row) => {
        const v = getCampo(row, 'observacoes');
        const editado = linhaEditada(row.periodo);
        return (
          <Input size="small" value={v as string}
            disabled={!podeEditar}
            onChange={(e) => setCampo(row, 'observacoes', e.target.value)}
            placeholder="opcional"
            maxLength={500}
            style={{
              borderColor: editado ? '#0930D0' : undefined,
            }} />
        );
      },
    },
    ...(podeEditar ? [{
      title: '', key: 'acoes', width: 50, align: 'center' as const,
      render: (_: any, row: MesMeta) => row.meta_valor > 0 ? (
        <Button size="small" danger icon={<DeleteOutlined />}
          onClick={() => excluirMeta(row)} title="Excluir meta deste mês" />
      ) : null,
    }] : []),
  ];

  const anoAtual = new Date().getFullYear();
  const anosDisponiveis = Array.from({ length: 5 }, (_, i) => anoAtual + 1 - i);
  const numEditadas = Object.keys(editados).length;

  return (
    <div style={{ padding: '16px 24px' }}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={12} style={{ marginBottom: 18 }}>
        <div>
          <Title level={3} style={{ margin: 0, color: '#020D7F' }}>
            <CalendarOutlined /> Metas Comerciais
          </Title>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {podeEditar
              ? <>Edite qualquer meta na linha e clique <strong>Salvar alterações</strong> ao final. Realizado é calculado automaticamente.</>
              : <>Visualização das metas e realizado mensal. Edição restrita a admin e diretor operador.</>}
          </Text>
        </div>
        <Space>
          {podeEditar && numEditadas > 0 && (
            <>
              <Tag color="blue" style={{ fontSize: 12, padding: '4px 10px' }}>
                {numEditadas} alteração(ões) pendente(s)
              </Tag>
              <Popconfirm title="Descartar todas as alterações?" onConfirm={descartarTudo}>
                <Button size="middle" icon={<UndoOutlined />} disabled={salvando}>Descartar</Button>
              </Popconfirm>
              <Button size="middle" type="primary" icon={<SaveOutlined />}
                loading={salvando} onClick={salvarTudo}
                style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}>
                {salvando && progresso ? `Salvando ${progresso.atual}/${progresso.total}…` : `Salvar ${numEditadas} alteração(ões)`}
              </Button>
            </>
          )}
          <Select value={ano} onChange={(v) => {
              if (numEditadas > 0) {
                modal.confirm({
                  title: 'Trocar de ano descarta as alterações pendentes. Continuar?',
                  okText: 'Trocar', cancelText: 'Cancelar',
                  onOk: () => setAno(v),
                });
              } else { setAno(v); }
            }} style={{ width: 120 }} size="middle"
            options={anosDisponiveis.map(a => ({ value: a, label: String(a) }))} />
        </Space>
      </Flex>

      {!podeEditar && (
        <Alert
          type="info" showIcon
          message="Modo somente leitura"
          description="Você pode visualizar metas e progresso. A edição é restrita aos perfis admin e diretor operador."
          style={{ marginBottom: 16 }}
        />
      )}

      {loading ? (
        <Flex justify="center" align="center" style={{ minHeight: 200 }}><Spin /></Flex>
      ) : !data ? (
        <Empty description="Erro ao carregar metas" />
      ) : (
        <>
          {/* Resumo do ano */}
          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={8}>
              <Card size="small">
                <Statistic title="Meta anual" value={fmt(data.totais.meta_valor)}
                  valueStyle={{ color: '#020D7F', fontSize: 20 }} prefix={<DollarOutlined />} />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small">
                <Statistic title="Realizado no ano" value={fmt(data.totais.realizado)}
                  valueStyle={{ color: '#0a7f4a', fontSize: 20 }} prefix={<ArrowUpOutlined />} />
                <Text type="secondary" style={{ fontSize: 11 }}>{data.totais.n_ganhos} negócios ganhos</Text>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small">
                <div style={{ marginBottom: 4 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Atingimento anual</Text>
                </div>
                {data.totais.pct_atingimento == null ? (
                  <Text type="secondary" italic>Sem metas cadastradas no ano</Text>
                ) : (
                  <Progress percent={Math.min(data.totais.pct_atingimento, 100)} status="active"
                    strokeColor={pctColor(data.totais.pct_atingimento)}
                    format={() => <Text strong style={{ color: pctColor(data.totais.pct_atingimento), fontSize: 16 }}>
                      {data.totais.pct_atingimento}%
                    </Text>} />
                )}
              </Card>
            </Col>
          </Row>

          {/* Tabela editável */}
          <Card size="small" title={<Text strong style={{ color: '#020D7F' }}>Metas mensais — {ano}</Text>}>
            <Table size="small" rowKey="periodo" pagination={false}
              columns={cols} dataSource={data.meses}
              rowClassName={(r) => linhaEditada(r.periodo) ? 'linha-editada' : ''}
            />
          </Card>

          <style>{`.linha-editada > td { background: #F0F5FF !important; }`}</style>

          <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 12 }}>
            💡 <strong>Como funciona:</strong> meta = valor que a empresa quer fechar no mês.
            Realizado = soma do valor_final das propostas com <code>status='ganho'</code> e
            data_fechamento dentro do mês. % atingimento = realizado / meta × 100.
            Permissão: <code>admin</code> e <code>diretor_operador</code>.
          </Paragraph>
        </>
      )}
    </div>
  );
}

export default function ComercialMetas() {
  return <AntApp><ComercialMetasInner /></AntApp>;
}
